import { useTranslation } from 'react-i18next';
import { Apple, Monitor, Download, Check, Info, Shield } from 'lucide-react';
import { useDeviceDetect } from '../hooks/useDeviceDetect';
import { buildDownloadUrl, getDownloadUrls, DOWNLOAD_VERSION } from '../downloads';
import { resolvePrivacyPdfPath } from '../legalLinks';
import { trackConversion } from '../analytics';
import { ScrollReveal } from './ScrollReveal';

interface DownloadOption {
  os: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  version: string;
  formats: { label: string; url: string; primary?: boolean }[];
  requirements?: string[];
  isRecommended?: boolean;
}

export function DownloadPage() {
  const { t, i18n } = useTranslation();
  const { os } = useDeviceDetect();
  const privacyPdfPath = resolvePrivacyPdfPath(i18n.language);

  const downloadOptions: DownloadOption[] = [
    {
      os: 'macos',
      icon: <Apple className="size-12" />,
      title: t('download.macosTitle'),
      description: t('download.macosDesc'),
      version: `v${DOWNLOAD_VERSION}`,
      formats: getDownloadUrls('macos').slice(0, 1),
      requirements: [
        t('download.macosReq1'),
        t('download.macosReq2'),
        t('download.macosReq3'),
      ],
      isRecommended: os === 'macos',
    },
    {
      os: 'windows',
      icon: <Monitor className="size-12" />,
      title: t('download.windowsTitle'),
      description: t('download.windowsDesc'),
      version: `v${DOWNLOAD_VERSION}`,
      formats: getDownloadUrls('windows').slice(0, 1),
      requirements: [
        t('download.windowsReq1'),
        t('download.windowsReq2'),
        t('download.windowsReq3'),
      ],
      isRecommended: os === 'windows',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
      {/* Header */}
      <section className="py-20" style={{ paddingTop: '140px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <ScrollReveal>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                {t('download.pageTitle')}
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <p className="text-xl text-gray-600">
                {t('download.pageSubtitle')}
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Download Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2" style={{ gap: '30px' }}>
            {downloadOptions.map((option, optionIndex) => (
              <ScrollReveal key={option.os} delay={optionIndex + 1}>
                <div 
                  id={`download-${option.os}`}
                  className={`relative bg-white rounded-2xl border-2 p-8 transition-all hover:shadow-xl ${
                    option.isRecommended 
                      ? 'border-teal-500 shadow-lg' 
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                  style={{
                    width: '65%',
                    margin: 0,
                    justifySelf: option.os === 'macos' ? 'end' : 'start',
                  }}
                >
                {/* Recommended Badge */}
                {option.isRecommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-teal-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {t('download.recommended')}
                    </span>
                  </div>
                )}

                {/* Icon & Title */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4 ${
                    option.isRecommended ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {option.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{option.title}</h2>
                  <p
                    className="text-gray-600 mt-2"
                    style={option.os === 'windows' ? { whiteSpace: 'pre-line' } : undefined}
                  >
                    {option.description}
                  </p>
                </div>

                {/* Version Info */}
                <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mb-6">
                  <span className="bg-gray-100 px-3 py-1 rounded-full">{option.version}</span>
                </div>

                {/* Download Buttons */}
                <div className="space-y-3 mb-8">
                  {option.formats.map((format, idx) => (
                    <a
                      key={idx}
                      href={format.url}
                      className={`flex items-center justify-center gap-2 px-6 rounded-lg font-semibold transition-colors text-lg ${
                        format.primary
                          ? 'bg-teal-600 hover:bg-teal-700 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      style={{ width: '92%', margin: '0 auto', paddingTop: '18px', paddingBottom: '18px' }}
                      onClick={() =>
                        trackConversion('download', {
                          source: 'download-page',
                          os: option.os,
                          format: format.label,
                        })
                      }
                    >
                      <Download className="size-4" />
                      {format.label}
                    </a>
                  ))}
                </div>

                {/* Requirements */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Info className="size-4 text-gray-500" />
                    {t('download.requirements')}
                  </h3>
                  <ul className="space-y-2">
                    {option.requirements?.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="size-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Additional Info */}
          <ScrollReveal delay={3}>
            <div className="mt-16 bg-white rounded-2xl border border-gray-200 p-8" style={{ marginTop: '48px' }}>
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 rounded-lg p-3">
                  <Shield className="size-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('download.securityTitle')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t('download.securityDesc')}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <a href={privacyPdfPath} className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
                      <Shield className="size-4" />
                      {t('download.privacyPolicy')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Mobile Warning */}
          <ScrollReveal delay={4}>
            <div
              className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6"
              style={{ marginTop: '24px', marginBottom: '40px' }}
            >
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 rounded-lg p-3">
                  <Monitor className="size-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-900 mb-1">
                    {t('download.desktopOnly')}
                  </h3>
                  <p className="text-amber-700">
                    {t('download.desktopOnlyDesc')}
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
